import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { l10n } from '@ott/l10n';
import {
  hooks as virtualCardsHooks,
  actions as virtualCardActions,
} from '@ott/shared-virtual-card-logic';
import KM from '@ott/utility-km';

import { unwrapResult } from '@reduxjs/toolkit';

import ErrorModal from '../ErrorModal';
import SuccessModal from '../SuccessModal';
import UpgradeForm from '../UpgradeFormModal';
import { FormData, FormError, UpgradeError } from '../UpgradeFormModal/types';
import TransferPromoModal from './blocks/TransferPromoModal';
import UpgradePromoModal from './blocks/UpgradePromoModal';

const {
  virtualCardFlow: { useVirtualCardFlow },
} = virtualCardsHooks;

type Props = {
  isOpen?: boolean;
  withPromo?: boolean;
  withTransferPromo?: boolean;
  kmParams?: Record<string, any>;
  preventUserCloseModal?: boolean;
  onClose: VoidFunction;
};

const UpgradeModal = ({
  isOpen = true,
  withPromo = true,
  withTransferPromo = false,
  kmParams = {},
  preventUserCloseModal = false,
  onClose,
}: Props) => {
  const [isPromoShown, setIsPromoShown] = useState(false);
  const [isTransferPromoShown, setIsTransferPromoShown] = useState(false);
  const [error, setError] = useState<FormError | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const dispatch = useDispatch<any>();

  const { openRefillModal } = useVirtualCardFlow();

  const handlePromoModalClose = useCallback(() => {
    KM('Virtual_Card_Features_Confirm_Upgrade_Press');
    setIsPromoShown(true);
  }, []);

  const handleTransferPromoModalClose = useCallback(() => {
    setIsTransferPromoShown(true);
  }, []);

  const handleSuccessModalActionButtonClick = useCallback(() => {
    KM('Virtual_Card_Issued_Success_Replenish_Balance_Uprid_Press');
    openRefillModal({ onClose, kmParams });
  }, [kmParams, onClose, openRefillModal]);

  const handleSuccessModalClose = useCallback(() => {
    KM('Virtual_Card_Issued_Success_Close_Uprid_Press');
    onClose();
  }, [onClose]);

  const onUpgradeFormSubmit = useCallback(
    (data: FormData) => {
      const { taxOk, ...updateFormData } = data;

      if (!taxOk) {
        setError('taxNotOk');

        return;
      }

      const params = {
        ...updateFormData,
        isTaxResidentOfRussianFed: taxOk,
        isTaxResidentOfOtherCountries: !taxOk,
        wasBornInUnitedStates: !taxOk,
        documentSeries: '',
      };

      dispatch(virtualCardActions.upgradeCard(params))
        .then(unwrapResult)
        .then(() => {
          KM('Virtual_Card_Issued_Success_Upgrade');
          setIsSuccess(true);
        })
        .catch((error: UpgradeError) => {
          setError(error.error);
          throw error;
        });
    },
    [dispatch]
  );

  const clearError = useCallback(() => setError(null), []);

  if (withPromo && !isPromoShown) {
    return (
      <UpgradePromoModal
        preventUserCloseModal={preventUserCloseModal}
        onClose={onClose}
        onAction={handlePromoModalClose}
      />
    );
  }

  if (withTransferPromo && !isTransferPromoShown) {
    return <TransferPromoModal onClose={onClose} onAction={handleTransferPromoModalClose} />;
  }

  if (isSuccess) {
    return (
      <SuccessModal
        title={l10n('virtualCard.upgradeSuccess.title')}
        description={l10n('virtualCard.upgradeSuccess.description')}
        actionButtonText={l10n('virtualCard.upgradeSuccess.action')}
        fullScreenOnMobile={true}
        preventUserCloseModal={preventUserCloseModal}
        onAction={handleSuccessModalActionButtonClick}
        onClose={handleSuccessModalClose}
      />
    );
  }

  if (error) {
    if (error === 'taxNotOk') {
      return (
        <ErrorModal
          preventUserCloseModal={preventUserCloseModal}
          title={l10n('virtualCard.errors.taxNotOk.title')}
          description={l10n('virtualCard.errors.taxNotOk.description')}
          actionButtonText={l10n('virtualCard.errors.taxNotOk.action')}
          onClose={clearError}
          onAction={clearError}
        />
      );
    }

    return (
      <ErrorModal
        preventUserCloseModal={preventUserCloseModal}
        title={l10n('virtualCard.errors.serverError.title')}
        description={l10n('virtualCard.errors.serverError.description')}
        actionButtonText={l10n('virtualCard.errors.serverError.action')}
        onClose={onClose}
        onAction={onClose}
      />
    );
  }

  return (
    <UpgradeForm
      preventUserCloseModal={preventUserCloseModal}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={onUpgradeFormSubmit}
    />
  );
};

export default React.memo(UpgradeModal);
